import { useState, useEffect, useCallback, useRef } from 'react'
import { fetchData } from '../../../reusable'
import moment from 'moment'

// ─── Types ───
interface SelectOption {
  value: string | number
  label: string
}

type TabType = 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'CUSTOM'

interface OccupancyParams {
  categoryId?: string | number
  actionId?: string | number
  type: TabType
  typeData?: string
  fromDate?: string
  toDate?: string
}

interface OccupancyDateItem {
  date: string
  dayName?: string
  occupancyRate: number
}

interface OccupancyData {
  categories: string[]
  rates: number[]
  overallRate: number
}

interface ListingOption {
  value: string | number
  label: string
}

// ─── Helpers ───
const safeApiCall = async (endpoint: string, params: Record<string, any>): Promise<any> => {
  try {
    const res = await fetchData(endpoint, params)
    if (res?.status == '200' && res?.data) return res.data
    return null
  } catch {
    return null
  }
}

// ─── Main Hook ───
export const useOccupancyRates = (
  category: SelectOption | null,
  listing: SelectOption | null,
  tab: TabType,
  weekStart: string,
  selectedMonth: string,
  selectedYear: SelectOption | null,
  fromDate: string,
  toDate: string,
) => {
  const [data, setData] = useState<OccupancyData>({ categories: [], rates: [], overallRate: 0 })
  const [isLoading, setIsLoading] = useState(false)
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  const fetchReport = useCallback(async () => {
    if (!category?.value) {
      setData({ categories: [], rates: [], overallRate: 0 })
      return
    }

    setIsLoading(true)

    const params: OccupancyParams = { type: tab, categoryId: category.value }
    if (listing?.value) params.actionId = listing.value

    if (tab === 'WEEKLY') {
      params.typeData = weekStart
    } else if (tab === 'MONTHLY') {
      params.typeData = selectedMonth
    } else if (tab === 'YEARLY') {
      params.typeData = String(selectedYear?.value ?? moment().year())
    } else if (tab === 'CUSTOM') {
      params.fromDate = fromDate
      params.toDate = toDate
    }

    const result = await safeApiCall('admin/viewOccupancyReport', params)

    if (!isMounted.current) return

    if (result?.[0]?.date && Array.isArray(result[0].date)) {
      const dateItems: OccupancyDateItem[] = result[0].date
      const overallRate = parseFloat(result[0]?.occupancyRate) || 0
      const rates = dateItems.map((item) => item?.occupancyRate ?? 0)

      let categories: string[] = []
      if (tab === 'WEEKLY') {
        categories = dateItems.map((item) => {
          const day = item?.dayName ?? ''
          const date = item?.date ?? ''
          // "01-06-2026" → "01 Jun"
          const formatted = date ? moment(date, 'DD-MM-YYYY').format('DD MMM') : date
          return `${day} ${formatted}`.trim()
        })
      } else if (tab === 'MONTHLY' || tab === 'CUSTOM') {
        // "01-06-2026" → "01"
        categories = dateItems.map((item) => {
          const date = item?.date ?? ''
          return date ? date.split('-')[0] : ''
        })
      } else if (tab === 'YEARLY') {
        categories = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      }

      setData({ categories, rates, overallRate })
    } else {
      setData({ categories: [], rates: [], overallRate: 0 })
    }

    setIsLoading(false)
  }, [category, listing, tab, weekStart, selectedMonth, selectedYear, fromDate, toDate])

  useEffect(() => {
    fetchReport()
  }, [fetchReport])

  return { data, isLoading }
}

// ─── Listing List Hook ───
export const useListingList = (categoryId: string | number | undefined): ListingOption[] => {
  const [list, setList] = useState<ListingOption[]>([])

  useEffect(() => {
    if (!categoryId) { setList([]); return }

    const endpointMap: Record<string, { endpoint: string; idKey: string; nameKey: string }> = {
      '1': { endpoint: 'admin/hotelList', idKey: 'hotelId', nameKey: 'en_name' },
      '4': { endpoint: 'admin/spacerentalList', idKey: 'spacerentalId', nameKey: 'en_name' },
      '5': { endpoint: 'admin/tourpackageList', idKey: 'tourpackageId', nameKey: 'en_agencyName' },
      '7': { endpoint: 'admin/carRentalList', idKey: 'carrentalId', nameKey: 'en_supplierName' },
    }

    const config = endpointMap[String(categoryId)]
    if (!config) { setList([]); return }

    const fetch = async () => {
      const data = await safeApiCall(config.endpoint, {})
      if (Array.isArray(data)) {
        setList(data.map((item: any) => ({
          value: item?.[config.idKey] ?? '',
          label: item?.[config.nameKey] || item?.en_name || item?.name || '-',
        })))
      } else {
        setList([])
      }
    }
    fetch()
  }, [categoryId])

  return list
}

// ─── Year List Generator ───
export const useYearList = (): SelectOption[] => {
  const [list] = useState<SelectOption[]>(() => {
    const currentYear = moment().year()
    return Array.from({ length: currentYear - 2020 + 1 }, (_, i) => ({
      value: String(currentYear - i),
      label: String(currentYear - i),
    }))
  })
  return list
}
